// src/components/LoginPage.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaUserCircle,
  FaEnvelope,
  FaEye,
  FaEyeSlash
} from 'react-icons/fa';
import { motion } from 'framer-motion';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../config';

import DarkVeil from './DarkVeil';
import ForgotPasswordChatbot from './ForgotPasswordChatbot'; // 🔥 Imported
import './LoginPage.css';
import logoImg from '../assets/M2_Lab_Logo.png';

export default function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [showForgotChatbot, setShowForgotChatbot] = useState(false); // ✅ State for chatbot

  useEffect(() => {
    const timeout = setTimeout(() => {
      document.querySelectorAll('input').forEach(input => {
        if (input.value) input.classList.add('not-empty');
        input.addEventListener('input', () => {
          if (input.value) {
            input.classList.add('not-empty');
          } else {
            input.classList.remove('not-empty');
          }
        });
      });
    }, 200);
    return () => clearTimeout(timeout);
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    const payload = { email, password };

    try {
      const response = await fetch(`${BASE_URL}/login`, { // change needed in Server Code
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success("Login successful!");
        setFailedAttempts(0);
        localStorage.setItem('userEmail', email);
        setTimeout(() => navigate('/upload'), 1500);
      } else {
        const errorData = await response.json();
        const newAttempts = failedAttempts + 1;
        setFailedAttempts(newAttempts);
        if (newAttempts >= 3) {
          toast.warning("Multiple failed attempts detected.");
        } else {
          toast.error(`Error: ${errorData.detail}`);
        }
      }
    } catch {
      toast.error("Server error. Please try again later.");
    }
  };

  return (
    <div className="login-page">
      <div className="darkveil-container">
        <DarkVeil />
      </div>

      <ToastContainer />

      {/* HEADER */}
      <header className="login-navbar">
        <div className="login-logo-container" onClick={() => navigate('/')}>
          <img src={logoImg} alt="M2 Lab Logo" className="login-logo-img" />
          <span className="login-lab-title">Purdue M2 Lab</span>
        </div>
      </header>

      {/* MAIN LOGIN BOX */}
      <div className="login-content">
        <motion.div
          className="login-box"
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <FaUserCircle size={60} className="login-icon-header" />
          <h2 className="login-heading">
            Please Login to access <br /> SynthGuard
          </h2>

          <form onSubmit={handleLogin} className="login-form">
            <div className="input-wrapper">
              <FaEnvelope className="input-icon" size={18} />
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Email Address"
                required
              />
            </div>

            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                required
              />
              <span
                className="password-toggle"
                onClick={() => setShowPassword(v => !v)}
              >
                {showPassword ? <FaEyeSlash size={18} /> : <FaEye size={18} />}
              </span>
            </div>

            <button type="submit" className="login-button">Login</button>
          </form>

          <div className="login-links">
            <button
              className="login-link-button"
              type="button"
              onClick={() => setShowForgotChatbot(true)} // 👉 Opens chatbot
            >
              Forgot Password?
            </button>
            <span> • </span>
            <button
              className="login-link-button"
              type="button"
              onClick={() => navigate('/create-account')}
            >
              Create Account
            </button>
          </div>
        </motion.div>
      </div>

      {/* FOOTER */}
      <footer className="login-footer">© 2025 Purdue University</footer>

      {/* CHATBOT - Forgot Password */}
      {showForgotChatbot && (
        <ForgotPasswordChatbot onClose={() => setShowForgotChatbot(false)} />
      )}
    </div>
  );
}
